import { useState, useEffect } from 'react'
import { api } from '../../services/api'
import type { RevenueResponse } from '../../services/api'

const CATEGORY_LABELS: Record<string, string> = {
  package: 'Paquetes',
  implementation: 'Implementaciones',
  consulting: 'Consultoria',
  offensive: 'Seguridad Ofensiva',
  license: 'Licencias',
  automation: 'Automatizacion'
}

const CATEGORY_COLORS: Record<string, string> = {
  package: '#9333ea',
  implementation: '#3b82f6',
  consulting: '#22c55e',
  offensive: '#ef4444',
  license: '#eab308',
  automation: '#06b6d4'
}

export default function RevenueDashboard() {
  const [data, setData] = useState<RevenueResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())

  const fetchRevenue = async () => {
    try {
      setLoading(true)
      setError(null)
      const result = await api.getRevenue({ year: selectedYear })

      if (!result.success) {
        throw new Error(result.error || 'Error al cargar datos de revenue')
      }

      setData(result.data || null)
    } catch (err) {
      console.error('Error fetching revenue:', err)
      setError(err instanceof Error ? err.message : 'Error al cargar datos')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchRevenue()
  }, [selectedYear])

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-jarix-orange mx-auto mb-4"></div>
        <p className="text-gray-400">Cargando datos de revenue...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded-lg">
        {error}
        <button onClick={fetchRevenue} className="ml-4 underline">Reintentar</button>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="text-center py-12 bg-gray-800 rounded-xl">
        <p className="text-gray-400">No hay datos de revenue disponibles</p>
      </div>
    )
  }

  const maxMonthlyRevenue = Math.max(...data.monthlyRevenue.map(m => m.revenue), 1)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white">Revenue & Analytics</h2>
          <p className="text-gray-400 text-sm mt-1">
            Analisis de ingresos y servicios vendidos
          </p>
        </div>
        <select
          value={selectedYear}
          onChange={(e) => setSelectedYear(parseInt(e.target.value))}
          className="bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600"
        >
          {[2024, 2025, 2026].map(year => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gray-800 rounded-xl p-5">
          <p className="text-gray-400 text-sm">Revenue Total</p>
          <p className="text-3xl font-bold text-jarix-orange mt-1">
            {formatCurrency(data.summary.totalRevenue)}
          </p>
          <p className="text-gray-500 text-xs mt-2">{selectedYear}</p>
        </div>

        <div className="bg-gray-800 rounded-xl p-5">
          <p className="text-gray-400 text-sm">Costo Total</p>
          <p className="text-3xl font-bold text-red-400 mt-1">
            {formatCurrency(data.summary.totalCost)}
          </p>
          <p className="text-gray-500 text-xs mt-2">Costos de proveedores</p>
        </div>

        <div className="bg-gray-800 rounded-xl p-5">
          <p className="text-gray-400 text-sm">Margen Bruto</p>
          <p className="text-3xl font-bold text-green-400 mt-1">
            {formatCurrency(data.summary.totalMargin)}
          </p>
          <p className="text-gray-500 text-xs mt-2">
            {data.summary.marginPercent.toFixed(1)}% del revenue
          </p>
        </div>

        <div className="bg-gray-800 rounded-xl p-5">
          <p className="text-gray-400 text-sm">Servicios Vendidos</p>
          <p className="text-3xl font-bold text-white mt-1">
            {data.summary.totalServices}
          </p>
          <p className="text-gray-500 text-xs mt-2">
            {data.summary.activeServices} activos actualmente
          </p>
        </div>
      </div>

      {/* Monthly Revenue Chart */}
      <div className="bg-gray-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Revenue Mensual {selectedYear}</h3>

        <div className="space-y-3">
          {data.monthlyRevenue.map((month) => (
            <div key={month.month} className="flex items-center gap-4">
              <div className="w-16 text-sm text-gray-400">{month.month}</div>
              <div className="flex-1">
                <div className="relative h-8 bg-gray-700 rounded-full overflow-hidden">
                  {/* Revenue bar */}
                  <div
                    className="absolute h-full bg-jarix-orange rounded-full transition-all duration-500"
                    style={{ width: `${(month.revenue / maxMonthlyRevenue) * 100}%` }}
                  />
                  {/* Cost overlay */}
                  <div
                    className="absolute h-full bg-red-500/30 rounded-full"
                    style={{ width: `${(month.cost / maxMonthlyRevenue) * 100}%` }}
                  />
                </div>
              </div>
              <div className="w-32 text-right">
                <span className="text-jarix-orange font-medium">{formatCurrency(month.revenue)}</span>
                <span className="text-gray-500 text-sm ml-2">({month.serviceCount})</span>
              </div>
            </div>
          ))}
        </div>

        <div className="flex gap-6 mt-4 pt-4 border-t border-gray-700 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-jarix-orange rounded"></div>
            <span className="text-gray-400">Revenue</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-red-500/30 rounded"></div>
            <span className="text-gray-400">Costo</span>
          </div>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue by Category */}
        <div className="bg-gray-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Revenue por Categoria</h3>

          <div className="space-y-4">
            {data.categoryRevenue.map((cat) => (
              <div key={cat.category}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-300">{CATEGORY_LABELS[cat.category] || cat.category}</span>
                  <span className="text-white font-medium">{formatCurrency(cat.revenue)}</span>
                </div>
                <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${cat.percentage}%`,
                      backgroundColor: CATEGORY_COLORS[cat.category] || '#6b7280'
                    }}
                  />
                </div>
                <p className="text-gray-500 text-xs mt-1">{cat.percentage.toFixed(1)}% del total</p>
              </div>
            ))}
          </div>

          {data.categoryRevenue.length === 0 && (
            <p className="text-gray-500 text-center py-4">No hay datos de categorias</p>
          )}
        </div>

        {/* Top Tenants */}
        <div className="bg-gray-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Top Clientes por Revenue</h3>

          <div className="space-y-3">
            {data.topTenants.map((tenant, index) => (
              <div
                key={tenant.id}
                className="flex items-center justify-between p-3 bg-gray-700/50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                    index === 0 ? 'bg-yellow-500' :
                    index === 1 ? 'bg-gray-400' :
                    index === 2 ? 'bg-amber-700' :
                    'bg-gray-600'
                  }`}>
                    {index + 1}
                  </div>
                  <div>
                    <p className="text-white font-medium">{tenant.name}</p>
                    <p className="text-gray-400 text-xs">{tenant.serviceCount} servicios</p>
                  </div>
                </div>
                <p className="text-jarix-orange font-semibold">
                  {formatCurrency(tenant.revenue)}
                </p>
              </div>
            ))}
          </div>

          {data.topTenants.length === 0 && (
            <p className="text-gray-500 text-center py-4">No hay datos de clientes</p>
          )}
        </div>
      </div>

      {/* Monthly Breakdown Table */}
      <div className="bg-gray-800 rounded-xl overflow-hidden">
        <div className="p-6 border-b border-gray-700">
          <h3 className="text-lg font-semibold text-white">Detalle Mensual</h3>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Mes</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-300 uppercase">Revenue</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-300 uppercase">Costo</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-300 uppercase">Margen</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-300 uppercase">Servicios</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Categorias</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {data.monthlyRevenue.map((month) => (
                <tr key={month.month} className="hover:bg-gray-700/50">
                  <td className="px-6 py-4 text-white font-medium">{month.month}</td>
                  <td className="px-6 py-4 text-right text-jarix-orange font-medium">
                    {formatCurrency(month.revenue)}
                  </td>
                  <td className="px-6 py-4 text-right text-red-400">
                    {formatCurrency(month.cost)}
                  </td>
                  <td className="px-6 py-4 text-right text-green-400">
                    {formatCurrency(month.margin)}
                  </td>
                  <td className="px-6 py-4 text-right text-gray-300">
                    {month.serviceCount}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-1 flex-wrap">
                      {Object.entries(month.byCategory).map(([cat, amount]) => (
                        <span
                          key={cat}
                          className="px-2 py-0.5 rounded text-xs"
                          style={{
                            backgroundColor: `${CATEGORY_COLORS[cat]}20`,
                            color: CATEGORY_COLORS[cat]
                          }}
                        >
                          {CATEGORY_LABELS[cat]?.slice(0, 4) || cat}: ${amount}
                        </span>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-700">
              <tr>
                <td className="px-6 py-4 text-white font-semibold">Total {selectedYear}</td>
                <td className="px-6 py-4 text-right text-jarix-orange font-bold">
                  {formatCurrency(data.summary.totalRevenue)}
                </td>
                <td className="px-6 py-4 text-right text-red-400 font-bold">
                  {formatCurrency(data.summary.totalCost)}
                </td>
                <td className="px-6 py-4 text-right text-green-400 font-bold">
                  {formatCurrency(data.summary.totalMargin)}
                </td>
                <td className="px-6 py-4 text-right text-gray-300 font-bold">
                  {data.summary.totalServices}
                </td>
                <td className="px-6 py-4"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Footer */}
      <div className="text-center text-gray-500 text-sm">
        Datos generados el {new Date(data.generatedAt).toLocaleString('es-CL')}
      </div>
    </div>
  )
}
